import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ApiError, api } from '../api/client';

export const PERMISSION_FIELDS = [
  'link_type',
  'bet_house_id',
  'traffic_source',
  'remarketing',
  'trigger',
  'source_id',
  'project_id',
  'creator_id',
] as const;
export type PermissionField = (typeof PERMISSION_FIELDS)[number];

export interface FieldPermission {
  field: PermissionField;
  editable: boolean;
  defaultValue: string | null;
}

export interface EntityVisibility {
  bet_house: string[];
  source: string[];
  project: string[];
  creator: string[];
}

export interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'manager' | 'viewer';
  pictureUrl: string | null;
}

interface AuthState {
  user: AuthUser | null;
  permissions: {
    fieldPermissions: FieldPermission[];
    entityVisibility: EntityVisibility;
    generatorAccess: {
      canAccessMain: boolean;
      canAccessTraffic: boolean;
      canAccessRedirectManager: boolean;
    };
  } | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Intervalo de revalidação em background. Fica abaixo do TTL de 5 min do access token
// do OTG, então uma mudança de role no OTG (ex.: admin→user no Suite) aparece na UI em
// ~1 TTL sem o usuário precisar deslogar. Ver INTEGRACAO.md §C3/§A6.
const REVALIDATE_MS = 2 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    permissions: null,
    loading: true,
  });

  // Carrega /auth/me e atualiza o estado.
  // - silent=false (load inicial / refresh forçado): qualquer falha limpa a sessão (mostra login).
  // - silent=true (revalidação em background): só limpa em 401 (sessão inválida / acesso removido
  //   no OTG); erros transitórios (rede/5xx) mantêm o estado atual, sem derrubar o usuário.
  const load = useCallback(async (silent: boolean) => {
    try {
      const data = await api<{ user: AuthUser; permissions: AuthState['permissions'] }>('/auth/me');
      // Roda a cada revalidação (2 min / foco / troca de rota). setUser é idempotente.
      Sentry.setUser({
        id: String(data.user.id),
        email: data.user.email,
        username: data.user.name,
      });
      setState({ user: data.user, permissions: data.permissions, loading: false });
    } catch (e) {
      const unauthorized = e instanceof ApiError && e.status === 401;
      if (!silent) {
        if (!(e instanceof ApiError)) console.error(e);
        Sentry.setUser(null);
        setState({ user: null, permissions: null, loading: false });
      } else if (unauthorized) {
        Sentry.setUser(null);
        setState({ user: null, permissions: null, loading: false });
      }
    }
  }, []);

  const refresh = useCallback(() => load(false), [load]);
  const revalidate = useCallback(() => load(true), [load]);

  useEffect(() => { void refresh(); }, [refresh]);

  const location = useLocation();
  const lastRevalidateRef = useRef(0);

  // Enquanto logado, revalida /auth/me periodicamente e ao focar a aba. Uma chamada a
  // /auth/me depois que o access token do OTG expira dispara o refresh no backend (que
  // re-deriva o role fresco do IdP) — é assim que a mudança de role chega na UI sozinha.
  const isAuthed = state.user !== null;
  useEffect(() => {
    if (!isAuthed) return;
    const tick = () => { lastRevalidateRef.current = Date.now(); void revalidate(); };
    const interval = window.setInterval(tick, REVALIDATE_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthed, revalidate]);

  // Revalida ao trocar de rota, no máximo 1x/60s (o intervalo/foco cobrem o resto).
  useEffect(() => {
    if (!isAuthed) return;
    if (Date.now() - lastRevalidateRef.current < 60_000) return;
    lastRevalidateRef.current = Date.now();
    void revalidate();
  }, [location.pathname, isAuthed, revalidate]);

  const logout = useCallback(async () => {
    try { await api('/auth/logout', { method: 'POST' }); } catch { /* ignora */ }
    Sentry.setUser(null);
    setState({ user: null, permissions: null, loading: false });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, logout, refresh }),
    [state, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
